import { MvxFactorId, MvxTri } from './MetabolicVulnerability';

/**
 * Possible Root Causes for the six measured MVX factors.
 *
 * THE INDICES ARE DELIBERATELY ABSENT. Schmidt: "There is no 'treating' the
 * MVX, IVX. They are a guide." The triage line already refuses to name a score,
 * and a Root Causes entry for MVX would contradict the panel beside it. Causes
 * belong to the six things actually measured.
 *
 * SHAPE follows the house Possible Root Causes tab exactly: each marker
 * DIRECTION gets two rows, "▲/▼ <Marker> — Functional Contributors" and
 * "— Medical / Diagnostic Associations". The split is not stylistic. A named
 * medical diagnosis goes in the Medical row even when the mechanism is what
 * makes it relevant; Functional is for mechanisms, exposures, medications,
 * intake and physiology (rule confirmed 2026-08-24).
 *
 * Static, like every other MVX module: MVX is not a Section, so it has no
 * FurtherConsiderations rows and no categoryId. A change here is a code change.
 *
 * THE THREE BCAAs SHARE ONE ENTRY PER DIRECTION. Their causes are identical,
 * the bullet rules already fire on "any BCAA", and Schmidt treats them as one
 * thing. Three copies of the same text would be worse, not more precise.
 *
 * ─────────────────────────── PROVENANCE ───────────────────────────
 *
 * EVERY BULLET CARRIES ITS SOURCE, and there are only three kinds:
 *
 *  1. A PAPER, cited inline as "— PMID: n" in the house Causes-tab form. Every
 *     one is also in MVX_REFERENCES, and a test enforces that.
 *  2. DR. SCHMIDT, named in the sentence. His teaching is a legitimate source
 *     here — the targets are his — but it has to read as his opinion rather
 *     than be dressed up as evidence.
 *  3. A STATEMENT ABOUT OUR OWN MODEL or the Labcorp report, verifiable from
 *     the code or from report 123601.
 *
 * The first pass (2026-10-07) failed this. An audit found 30 of 69 bullets were
 * general clinical knowledge with no source at all, and one sentence attributed
 * an enumeration to Dr. Schmidt that he never gave. Everything was then sourced
 * against PubMed, and what could not be sourced was CUT rather than softened:
 *
 *   dropped, no citation found — low physical activity, very low fat intake and
 *   insulin-resistant physiology as causes of a low Small HDL-P; chronic liver
 *   disease and nephrotic syndrome as Small HDL-P associations; toxic load,
 *   alcohol and blue-top tube contamination as causes of a high citrate; inborn
 *   errors of metabolism for citrate; malabsorption (celiac disease, IBD,
 *   pancreatic insufficiency, post-bariatric) for a low BCAA.
 *
 * Several of those are standard clinical associations and may well be true.
 * They are out because a practitioner-facing cause list is worth what its
 * weakest bullet is worth, not because they were judged wrong.
 *
 * ⚠ TWO ENTRIES ARE THIN, and they are thin in different ways. Low citrate has
 * no literature at all, no mechanism published and no outcome. What it has is
 * Dr. Schmidt's own clinical reading, given 2026-10-13, and the row says so. High Small HDL-P has
 * a MECHANISM (impaired cholesterol efflux) and a weak outcome signal, but no
 * established harm, which is not the same as no established cause — the entry
 * used to open by saying there was none and then name one two bullets later
 * (Maryann, 2026-10-09). Both are edges the bars draw because Schmidt states a
 * band rather than because the evidence demands one.
 */
export interface MvxRootCauseEntry {
  /** The factor(s) this entry covers. Three for the BCAA group, one otherwise. */
  markers: MvxFactorId[];
  /** Which side of the target band fires it. */
  direction: Exclude<MvxTri, 'opt' | 'absent'>;
  /** Marker name as it appears in the row titles, e.g. "GlycA". */
  label: string;
  /** Mechanisms, exposures, medications, intake, physiology. */
  functional: string[];
  /** Named diagnoses and disease associations. */
  medical: string[];
}

export const MVX_ROOT_CAUSES: MvxRootCauseEntry[] = [
  // ─────────────────────────────── GlycA ───────────────────────────────
  // HIGH ONLY. A low GlycA is never counted against anyone, so the model's
  // markerState never returns 'low' for it and no low entry can ever fire.
  {
    markers: ['glycA'],
    direction: 'high',
    label: 'GlycA',
    functional: [
      'Active or smoldering infection. Dr. Schmidt reads a high GlycA as fibrin-associated inflammation and/or infection, and in one of his cases a persistent elevation traced back to lung pain and sinus problems. A raised GlycA also predicts infection still to come, particularly septicaemia and pneumonia — PMID: 27136058.',
      'A chronic inflammatory state rather than a passing one. In more than 10,000 people, GlycA stayed elevated within the same individual for up to a decade, tracking a whole panel of inflammatory cytokines and a gene signature of increased neutrophil activity — PMID: 27136058.',
      'Fibrin-associated inflammation and a hypercoagulation tendency. Dr. Schmidt’s suggested follow-up is a hypercoagulation panel.',
      'Food-antigen or intestinal-barrier immune activation. Dr. Schmidt suggests Cyrex Array 12 here.',
      'Smoking, with a dose-response. Across 11,509 people, current smokers ran 19.9 µmol/L above never-smokers, each 5 pack-years added to the level, and it fell again the longer someone had quit — PMID: 28838917.',
      'Excess body weight. GlycA is elevated in severe obesity and falls steadily after bariatric surgery, reaching levels no different from normal-BMI controls by 12 months even though 41% of those patients still had a BMI above 30 — PMID: 30047224.',
      'Worth knowing when the number moves slowly: GlycA is a glycan signal rather than a single protein, reflecting N-glycan branching on circulating acute-phase proteins — PMID: 36254612. It also has lower within-person variability than hs-CRP — PMID: 30047224.',
    ],
    medical: [
      'Chronic low-grade systemic inflammation. Raised GlycA predicts future cardiovascular events — PMID: 25249300. It also predicts total mortality, non-cardiovascular inflammatory events and total cancer events — PMID: 27173011.',
      'Rheumatoid arthritis and other chronic inflammatory disease. GlycA runs higher in RA than in controls, tracks disease activity by DAS28, and each quartile increase raised the odds of coronary artery calcium by 48% — PMID: 25956924.',
      'Type 2 diabetes. GlycA predicted incident type 2 diabetes in 4,524 people where hs-CRP did not — PMID: 26549655. In established diabetes it predicted nephropathy, retinopathy and neuropathy, again where hs-CRP did not — PMID: 36757630.',
      'Chronic kidney disease. GlycA was associated with progression through CKD risk categories in generally healthy young and middle-aged adults — PMID: 36565799.',
      'Cancer. In 27,524 initially healthy women followed a median of 20.5 years, GlycA predicted cancer mortality as well as all-cause and cardiovascular mortality — PMID: 26951635.',
    ],
  },

  // ───────────────────────────── Small HDL-P ─────────────────────────────
  {
    markers: ['sHdlP'],
    direction: 'low',
    label: 'Small HDL-P',
    functional: [
      'Toxic burden and impaired detoxification. This is Dr. Schmidt’s first reading of a low value: test toxin burden, and stop alcohol.',
      'Smoking. In an NMR lipoprotein study of 612 adults, female smokers carried lower total and large HDL particle concentrations than non-smokers, independent of alcohol, diet, physical activity and weight; male smokers differed little — PMID: 21122700.',
      'Consumption by active inflammation. When GlycA is high as well, LabSmarts reads the low value as inflammation rather than toxic burden, and treating the inflammation comes first.',
    ],
    medical: [
      'Increased infectious-disease morbidity and mortality. In 30,195 people followed 15 years, the lowest percentile carried a hazard ratio of 2.31 for any infection and 3.23 for infection death — PMID: 36537045.',
      'Type 1 and type 2 diabetes with impaired cholesterol efflux capacity — PMID: 30567548.',
    ],
  },
  {
    markers: ['sHdlP'],
    direction: 'high',
    label: 'Small HDL-P',
    functional: [
      'Impaired cholesterol efflux can raise the small-particle count while total HDL still reads normal — PMID: 30567548.',
      'NO ESTABLISHED HARM. Nothing published says a high count is dangerous. The one signal, in the same 30,195-person cohort, is that the HIGHEST percentile still carried a hazard ratio of 1.36 for infection, small but with a confidence interval that excludes 1 — PMID: 36537045. Their reference group was the 91st to 95th percentile, so the optimum sits high in the distribution and short of the extreme.',
    ],
    medical: [
      'Type 1 diabetes with reduced cholesterol efflux capacity, where small HDL particles ran higher than in controls — PMID: 30567548.',
      'Nothing else is established. Treat a high value as a prompt to look at the rest of the panel rather than as a finding of its own.',
    ],
  },

  // ─────────────────────────────── Citrate ───────────────────────────────
  {
    markers: ['citrate'],
    direction: 'high',
    label: 'Citrate',
    functional: [
      'Mitochondrial dysfunction. This is the reading Dr. Schmidt teaches, and circulating citrate is used as a proxy for it in the literature — PMID: 39684514.',
      'Thiamine (B1) insufficiency. Dr. Schmidt’s response to citrate in either direction is mitochondrial support and B1, and he describes MMX as malnutrition of the most important macronutrient, protein, and the most important micronutrient, B1.',
      'Reduced kidney function. Citrate rose as eGFR fell in the end-stage liver disease cohort — PMID: 39684514.',
      'Repeat before acting on a single value. Dr. Schmidt: "Citrate can bounce around."',
    ],
    medical: [
      'End-stage liver disease. Plasma citrate ran about 40% above a community cohort, FELL after liver transplantation, and the highest tertile carried an increased risk of all-cause mortality — PMID: 39684514.',
      'All-cause mortality risk generally. Of 106 NMR biomarkers profiled in 17,345 people, citrate was one of only four that predicted death after adjustment for conventional risk factors, at a hazard ratio of 1.33 per standard deviation — PMID: 24586121.',
    ],
  },
  {
    markers: ['citrate'],
    direction: 'low',
    label: 'Citrate',
    functional: [
      'NO PUBLISHED SUPPORT. A low plasma citrate is not a described clinical state. The hypocitraturia literature is about URINE citrate, a different analyte answering a different question.',
      'A need for mitochondrial nutrients. This is Dr. Schmidt’s reading of a low citrate, and he treats it as he treats a high one: mitochondrial support and B1. The direction does not change his response.',
    ],
    medical: [
      'None established. Low URINARY citrate is associated with calcium stones and with distal renal tubular acidosis, where alkaline urine, hypocitraturia and hypercalciuria form the lithogenic triad — PMID: 30139463. That is urine citrate, and the association does not transfer to a plasma value.',
    ],
  },

  // ──────────────────────── Valine, Leucine, Isoleucine ────────────────────────
  {
    markers: ['valine', 'leucine', 'isoleucine'],
    direction: 'high',
    label: 'Valine, Leucine and Isoleucine',
    functional: [
      'Reduced insulin sensitivity with impaired BCAA catabolism. This is the landmark finding: a branched-chain amino acid signature separated obese from lean people and correlated with insulin resistance — PMID: 19356713.',
      'BCAA supplementation or heavy protein intake ON TOP OF a high-fat diet. In the animal arm of the same study, adding BCAAs to a high-fat diet produced insulin resistance with no extra weight gain, while adding them to standard chow did not — PMID: 19356713.',
      'Reduced activity of branched-chain ketoacid dehydrogenase, the enzyme that clears all three. Low BCKD activity is the main reason BCAAs rise, and it is part of why they run high in type 2 diabetes and obesity — PMID: 29755574. Dr. Schmidt’s response is high-dose B1 in either direction.',
      'Brief starvation. BCAAs rise rather than fall early in a fast, through amination of the branched-chain keto acids in visceral tissues and reduced uptake by muscle — PMID: 29755574.',
      'LabSmarts flags the group when any one of the three is above target. They usually move together, so a single outlier is worth repeating before acting on it.',
    ],
    medical: [
      'Insulin resistance and type 2 diabetes — PMID: 19356713. Whether raised BCAAs are only a marker of insulin resistance or also contribute to it is still unsettled — PMID: 29755574.',
      'Obesity — PMID: 29755574.',
      'Type 1 diabetes, by the same amination route as starvation — PMID: 29755574.',
      'Maple syrup urine disease, where BCKD activity is deficient. The extreme case: 184 patients studied over three decades, with leucine the amino acid that drives the phenotype — PMID: 31980395, 29755574.',
    ],
  },
  {
    markers: ['valine', 'leucine', 'isoleucine'],
    direction: 'low',
    label: 'Valine, Leucine and Isoleucine',
    functional: [
      'Inadequate protein intake. Dr. Schmidt’s response is to eat more protein, meat or BCAA powder, on a high-satiety diet.',
      'Impaired digestion and absorption, which Dr. Schmidt makes the priority when the BCAAs are low. "Fix digestion" in his framing means the pituitary, the vagus nerve, the stomach, the small and large intestine and the pancreas.',
      'A catabolic state burning them faster than they are replaced. BCAA oxidation is driven up in chronic renal failure, trauma, burns, sepsis, cancer and during exercise — PMID: 29755574. Dr. Schmidt treats cachexia with raw animal foods.',
      'Age-related undernutrition. In 189 community-dwelling older adults, branched-chain amino acids were INVERSELY associated with sarcopenia, alongside low mini-nutritional-assessment scores — PMID: 30624690.',
      'Thiamine (B1) insufficiency, same as the high side. Dr. Schmidt’s high-dose B1 applies in both directions.',
      'What the correction looks like: in 155 malnourished elderly people, a BCAA-enriched mixture raised mitochondrial ATP production, cognitive score and physical performance against diet advice alone — PMID: 31672329.',
    ],
    medical: [
      'Sarcopenia and frailty — PMID: 30624690.',
      'Protein-energy malnutrition, which travelled with low BCAAs and low mini-nutritional-assessment scores in the same older-adult cohort — PMID: 30624690.',
      'Liver cirrhosis. BCAAs fall because skeletal muscle consumes them to make glutamate for ammonia detoxification — PMID: 23756281, 29755574.',
      'Chronic renal failure, trauma, burns, sepsis and cancer, through increased BCKD activity and faster BCAA oxidation — PMID: 29755574.',
      'Urea cycle disorders, by the same ammonia-detoxification route as cirrhosis — PMID: 29755574.',
    ],
  },
];

/** The arrow that heads a row title, matching the house Causes tab. */
export const DIRECTION_ARROW: { [k: string]: string } = { high: '▲', low: '▼' };

/** "▲ GlycA — Functional Contributors", exactly as the house tab titles read. */
export function rootCauseTitle(entry: MvxRootCauseEntry, row: 'functional' | 'medical'): string {
  const suffix = row === 'functional' ? 'Functional Contributors' : 'Medical / Diagnostic Associations';
  return `${DIRECTION_ARROW[entry.direction]} ${entry.label} — ${suffix}`;
}
